import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { resetPassword } from "../../api/client";
import "../Login/login.scss";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = useMemo(() => searchParams.get("email")?.trim() || "", [searchParams]);
  const token = useMemo(() => searchParams.get("token")?.trim() || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const missingResetDetails = !email || !token;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (missingResetDetails) {
      setErrorMessage("This reset link is invalid or incomplete.");
      return;
    }

    if (!password || !passwordConfirmation) {
      setErrorMessage("Please enter and confirm your new password.");
      return;
    }

    if (password !== passwordConfirmation) {
      setErrorMessage("Password confirmation does not match.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await resetPassword({
        email,
        token,
        password,
        passwordConfirmation,
      });

      navigate("/login", {
        replace: true,
        state: { notice: "Password reset successful" },
      });
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__card">
        <div className="login-page__brand" aria-label="Hin Ho Lee Trading Sdn. Bhd.">
          <span>興和利貿易有限公司</span>
          <strong>Hin Ho Lee Trading Sdn. Bhd.</strong>
        </div>

        <h1 className="login-page__title login-page__title--compact">Reset password</h1>
        <p className="login-page__subtitle login-page__subtitle--compact">
          {missingResetDetails
            ? "This reset link is invalid or has expired."
            : `Choose a new password for ${email}.`}
        </p>

        {!missingResetDetails ? (
          <form className="login-page__form" onSubmit={handleSubmit}>
            <div className="login-page__field">
              <span className="login-page__icon">
                <Icon icon="ph:lock-fill" height={18} />
              </span>
              <input
                className="login-page__input"
                type="password"
                placeholder="New password"
                value={password}
                autoComplete="new-password"
                onChange={(event) => {
                  setPassword(event.target.value);
                  setErrorMessage("");
                }}
              />
            </div>

            <div className="login-page__field">
              <span className="login-page__icon">
                <Icon icon="ph:lock-key-fill" height={18} />
              </span>
              <input
                className="login-page__input"
                type="password"
                placeholder="Confirm password"
                value={passwordConfirmation}
                autoComplete="new-password"
                onChange={(event) => {
                  setPasswordConfirmation(event.target.value);
                  setErrorMessage("");
                }}
              />
            </div>

            {errorMessage && (
              <div className="login-page__message login-page__message--error">
                {errorMessage}
              </div>
            )}

            <button type="submit" className="login-page__submit" disabled={isSubmitting}>
              {isSubmitting ? "Resetting..." : "Reset password"}
            </button>
          </form>
        ) : (
          <>
            {errorMessage && (
              <div className="login-page__message login-page__message--error">
                {errorMessage}
              </div>
            )}
            <Link className="login-page__submit login-page__submit-link" to="/forgot-password">
              Request a new reset link
            </Link>
          </>
        )}

        <p className="login-page__footer-link">
          <Link className="login-page__link" to="/login">
            Back to login
          </Link>
        </p>
      </div>

      <div className="login-page__wave login-page__wave--one" />
      <div className="login-page__wave login-page__wave--two" />
    </div>
  );
};

export default ResetPasswordPage;
